import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Pagination, Stack, Typography } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ProductsGrid from './ProductsGrid';
import CategoryFilters from './CategoryFilters';
import ErrorNotice from '../common/ErrorNotice';
import axios from '../../api/axiosClient';

// Должно совпадать с REST_FRAMEWORK.PAGE_SIZE в backend/lliviling/lliviling/settings.py.
// Используется только как запасной вариант — как только приходит первая
// страница, размер страницы считается по факту пришедших данных.
const DEFAULT_PAGE_SIZE = 17;

const ProductsPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(Number(searchParams.get('page') || 1), 1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [redirectedTo404, setRedirectedTo404] = useState(false);

  const fetchProducts = useCallback(async (pageNum, category, subcategory) => {
    setLoading(true);
    const url = subcategory
      ? `/api/compositions/?page=${pageNum}&categories=${category}&categories=${subcategory}`
      : `/api/compositions/?page=${pageNum}&categories=${category}`;

    try {
      const response = await axios.get(url);
      const pageSize = (pageNum === 1 && response.data.results?.length) || DEFAULT_PAGE_SIZE;
      const calculatedTotalPages = response.data.count
        ? Math.max(1, Math.ceil(response.data.count / pageSize))
        : 1;

      setProducts(response.data.results || []);
      setTotalPages(calculatedTotalPages);
      setError('');
    } catch (err) {
      if (!redirectedTo404 && err.response && (err.response.status === 400 || err.response.status === 404)) {
        setRedirectedTo404(true);
        navigate('/404');
      } else {
        setError('Не удалось загрузить товары. Попробуйте обновить страницу.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, redirectedTo404]);

  useEffect(() => {
    fetchProducts(currentPage, categorySlug, subcategorySlug);
  }, [currentPage, categorySlug, subcategorySlug, fetchProducts]);

  const handleFilterChange = () => {
    setSearchParams({});
  };

  const handlePageChange = (_, newPage) => {
    const nextParams = new URLSearchParams(searchParams);

    if (newPage === 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(newPage));
    }

    setSearchParams(nextParams);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <CategoryFilters
        onFilterChange={handleFilterChange}
        categorySlug={categorySlug}
        subcategorySlug={subcategorySlug}
      />
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : products.length ? (
        <>
          <ProductsGrid products={products} />
          <Stack alignItems="center" sx={{ my: 4 }}>
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, totalPages)}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Stack>
        </>
      ) : (
        !error && (
          <Typography align="center" sx={{ py: 8 }}>
            Товары не найдены.
          </Typography>
        )
      )}
    </Box>
  );
};

export default ProductsPage;
